import { useInfiniteQuery } from 'react-query'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import { sendToApi } from 'api/client'
import { getNextPageParam } from 'utils/getNextPageParam'
import { reportError } from 'utils/error'
import { Filters } from './useFilters'
import { ArticleTodosGroup } from 'types'
import qs from 'qs'
import { useAuth } from 'hooks/UseAuth'
import { Response } from 'request-dot-js'
import { Sorting } from './useSorting'

interface Props {
  workspaceId?: string
  namespace?: string
  filters: Filters
  sorting?: Sorting
  items?: number
}

export const TASKS_ALL_NAMESPACE = 'tasks_all'

export const workspaceTasksKeys = {
  tasksAll: (workspaceKey: string) => ['workspaces', workspaceKey, TASKS_ALL_NAMESPACE],
  allItems: (workspaceKey: string, filters: Filters) => ['workspaces', workspaceKey, 'tasks', filters, 20],
  allItemsCount: (workspaceKey: string, filters: Filters) => [
    'workspaces',
    workspaceKey,
    'tasks',
    { ...filters, types: [] },
    1,
  ],
  forType: (workspaceKey: string, filters: Filters, type: string) => [
    'workspaces',
    workspaceKey,
    'tasks',
    { ...filters, types: [type] },
    1,
  ],
  forRole: (workspaceKey: string, filters: Filters, role: string) => [
    'workspaces',
    workspaceKey,
    'tasks',
    { ...filters, role },
    1,
  ],
  navMenuCounter: (workspaceKey: string) => [
    'workspaces',
    workspaceKey,
    'tasks',
    { state: 'open', types: [], role: 'assignee' },
    1,
  ],
  recentlyClosed: (workspaceKey: string, filters: Filters) => [
    'workspaces',
    workspaceKey,
    'tasks',
    { ...filters, state: 'closed' },
    1,
  ],
  completed: (workspaceKey: string, filters: Filters) => [
    'workspaces',
    workspaceKey,
    'tasks',
    { ...filters, review: 'submitted' },
    1,
  ],
}

export type Item = InformalFeedbackRequest | ArticleTodosGroup
type QueryKey = [string, string, string, Filters, Sorting | string, number]

export const useWorkspaceTasks = ({ workspaceId, namespace, filters, sorting, items = 20 }: Props) => {
  const auth = useAuth()

  return useInfiniteQuery<Response<Item[]>, Error, Response<Item[]>, QueryKey>(
    ['workspaces', workspaceId || '', namespace || 'tasks', filters, sorting || '', items],
    ({ pageParam = 1, queryKey }) => {
      const queryWorkspaceId = queryKey[1]
      const { role, ...queryFilters } = queryKey[3]
      const queryString = qs.stringify(
        {
          ...queryFilters,
          assignee: role === 'assignee' ? auth?.id : undefined,
          creator: role === 'creator' ? auth?.id : undefined,
          sorting_field: sorting?.field,
          sorting_order: sorting?.order,
          items,
          page: pageParam,
        },
        {
          indices: false,
          arrayFormat: 'comma',
        },
      )

      return sendToApi<Item[]>(`/w/${queryWorkspaceId}/tasks?${queryString}`)
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      getNextPageParam,
      enabled: !!workspaceId,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
    },
  )
}
